import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Form, Button, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBalanceScale, faTrash } from "@fortawesome/free-solid-svg-icons";

const euros = (cents) => `${((cents || 0) / 100).toFixed(2).replace(".", ",")} €`;

/**
 * Panneau « barème » : les questions propres à la catégorie choisie, puis le geste
 * proposé, que l'agent applique d'un clic — ou pas. Rien n'est jamais appliqué
 * automatiquement : le barème propose, l'agent décide.
 */
/**
 * Les lignes choisies, à plat, pour être enregistrées sur le SAV. Jusqu'ici seule
 * leur somme remontait (le montant de la question) et les libellés étaient
 * perdus : impossible de dire « désolés pour l'oubli de votre quiche » ni de
 * lister les produits concernés dans un récap.
 */
const flattenPicked = (map) =>
  Object.entries(map || {}).reduce((all, [questionKey, lines]) => {
    (lines || []).forEach((line) => {
      all.push({
        question: questionKey,
        key: line.key,
        type: line.type,
        label: line.label,
        amount: line.amount,
        quantity: line.qty,
      });
    });
    return all;
  }, []);

const BaremeSuggestion = (props) => {
  const { suggestion, answers, onAnswerChange, onApply, orderItems, onShowBareme, onPickedChange } = props;

  // Le prix demandé se trouve dans la commande : on le fait choisir dans une
  // liste plutôt que de laisser l'agent chercher le tarif du produit. Les deux
  // familles sont toujours proposées — un oubli peut porter sur un produit comme
  // sur un ingrédient — mais celle qu'attend la question passe en premier.
  const groupsFor = (source) => {
    const produits = (orderItems || []).filter((item) => item.type === "product");
    const ingredients = (orderItems || []).filter((item) => item.type === "ingredient");
    const groups = [
      { key: "product", label: "Produits de la commande", items: produits },
      { key: "ingredient", label: "Ingrédients", items: ingredients },
    ].filter((group) => group.items.length > 0);

    return source ? [...groups].sort((a, b) => (a.key === source ? -1 : b.key === source ? 1 : 0)) : groups;
  };

  // Plusieurs produits peuvent être concernés par le même SAV : les choix
  // s'additionnent, et chacun se retire.
  const [picked, setPicked] = useState({});

  // Changement de catégorie : le parent vide les réponses, on vide les choix.
  useEffect(() => {
    setPicked((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        if (answers[key] !== undefined && answers[key] !== "") next[key] = prev[key];
      });
      return next;
    });
  }, [answers]);

  // Une ligne par produit, avec sa quantité : deux fois la même quiche fait une
  // ligne « 2 × », pas deux lignes identiques qu'on ne sait plus distinguer.
  const sumOf = (list) => (list || []).reduce((total, line) => total + line.amount * line.qty, 0);

  const applyLines = (questionKey, lines) => {
    const next = { ...picked, [questionKey]: lines };
    setPicked(next);
    onAnswerChange(questionKey, lines.length ? (sumOf(lines) / 100).toFixed(2) : "");
    // Classification offrable du produit choisi : c'est elle qui permet au
    // barème de proposer « le même produit offert » sur un oubli. Toujours
    // renvoyée, vide comprise, pour qu'un choix retiré n'en laisse pas une
    // périmée derrière lui. Les ingrédients n'en portent pas : un ingrédient
    // offert est un geste unique, le barème le connaît sans qu'on le lui dise.
    const offerLine = (lines || []).find((line) => !!line.offerKey);
    onAnswerChange(`${questionKey}Offer`, offerLine ? offerLine.offerKey : "");
    // Plafond : le prix d'UN produit. Deux produits oubliés à 5 € font 10 €
    // d'avoir, mais on ne propose pas un produit à 10 € pour autant.
    // ⚠️ En CENTIMES, contrairement aux montants saisis par l'agent : l'API ne
    // convertit en centimes que les quatre clés de montant qu'elle connaît.
    onAnswerChange(`${questionKey}OfferMax`, offerLine ? String(offerLine.amount) : "");
    if (onPickedChange) {
      onPickedChange(flattenPicked(next));
    }
  };

  const addPicked = (questionKey, item) => {
    const lines = picked[questionKey] || [];
    const existing = lines.findIndex((line) => line.key === item.key);
    const next = existing >= 0
      ? lines.map((line, i) => (i === existing ? { ...line, qty: line.qty + 1 } : line))
      : [...lines, { ...item, qty: 1 }];
    applyLines(questionKey, next);
  };

  const changeQty = (questionKey, index, delta) => {
    const lines = picked[questionKey] || [];
    const target = lines[index];
    if (!target) return;
    if (target.qty + delta <= 0) {
      applyLines(questionKey, lines.filter((_, i) => i !== index));
      return;
    }
    applyLines(questionKey, lines.map((line, i) => (i === index ? { ...line, qty: line.qty + delta } : line)));
  };

  const removePicked = (questionKey, index) => {
    applyLines(questionKey, (picked[questionKey] || []).filter((_, i) => i !== index));
  };

  if (!suggestion) return null;

  const { status, cagnotteAmount, productOptions, explanation, warning, questions } = suggestion;
  const canApply = status === "proposal" && (cagnotteAmount > 0 || (productOptions || []).length > 0);

  return (
    <div className="mb-3 p-3" style={{ background: "#f7f9f7", border: "1px solid #dfe7e0", borderRadius: 6 }}>
      <div className="mb-2 d-flex align-items-center">
        <FontAwesomeIcon icon={faBalanceScale} className="mr-2" />
        <b>Barème</b>
        <Button variant="link" size="sm" className="ml-auto p-0" onClick={onShowBareme}>
          Voir le barème
        </Button>
      </div>

      {(questions || []).length > 0 && (
        <Row>
          {questions.map((q) => (
            <Col xs={12} md={6} key={q.key} className="mb-2">
              {q.type === "bool" ? (
                <Form.Check
                  type="checkbox"
                  id={`bareme-${q.key}`}
                  label={q.label}
                  checked={answers[q.key] === undefined ? !!q.default : !!answers[q.key]}
                  onChange={(e) => onAnswerChange(q.key, e.target.checked)}
                />
              ) : q.type === "choice" ? (
                <>
                  <Form.Label className="mb-1" style={{ fontSize: "0.85rem" }}>{q.label}</Form.Label>
                  <Form.Control
                    as="select"
                    value={answers[q.key] || ""}
                    onChange={(e) => onAnswerChange(q.key, e.target.value)}
                  >
                    <option value="">— Choisir —</option>
                    {(q.options || []).map((o) => (
                      <option key={o.key} value={o.key}>{o.label}</option>
                    ))}
                  </Form.Control>
                </>
              ) : (
                <>
                  <Form.Label className="mb-1" style={{ fontSize: "0.85rem" }}>
                    {q.label}{q.type === "amount" ? " (€)" : ""}
                  </Form.Label>
                  {q.type === "amount" && groupsFor(q.source).length > 0 && (
                    <Form.Control
                      as="select"
                      className="mb-1"
                      value=""
                      onChange={(e) => {
                        const chosen = (orderItems || []).find((item) => item.key === e.target.value);
                        if (chosen) addPicked(q.key, chosen);
                      }}
                    >
                      <option value="">
                        {(picked[q.key] || []).length ? "— Ajouter un autre élément —" : "— Choisir dans la commande —"}
                      </option>
                      {groupsFor(q.source).map((group) => (
                        <optgroup key={group.key} label={group.label}>
                          {group.items.map((item) => (
                            <option key={item.key} value={item.key}>
                              {item.label} — {(item.amount / 100).toFixed(2).replace(".", ",")} €
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </Form.Control>
                  )}
                  {(picked[q.key] || []).length > 0 && (
                    <div className="sav-picked mb-2">
                      <div className="sav-picked-title">Ce qui est concerné</div>
                      {(picked[q.key] || []).map((line, idx) => (
                        <div className="sav-picked-line" key={line.key}>
                          <div className="sav-picked-qty">
                            <Button variant="light" size="sm" onClick={() => changeQty(q.key, idx, -1)} title="Un de moins">−</Button>
                            <span>{line.qty}</span>
                            <Button variant="light" size="sm" onClick={() => changeQty(q.key, idx, 1)} title="Un de plus">+</Button>
                          </div>
                          <div className="sav-picked-label">{line.label}</div>
                          <div className="sav-picked-amount">
                            {((line.amount * line.qty) / 100).toFixed(2).replace(".", ",")} €
                          </div>
                          <Button
                            variant="link"
                            className="sav-picked-remove"
                            onClick={() => removePicked(q.key, idx)}
                            title="Retirer ce produit"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </div>
                      ))}
                      <div className="sav-picked-total">
                        <span>Total</span>
                        <b>{(sumOf(picked[q.key]) / 100).toFixed(2).replace(".", ",")} €</b>
                      </div>
                    </div>
                  )}
                  <Form.Label className="mb-1" style={{ fontSize: "0.8rem", color: "#6c757d" }}>
                    {(picked[q.key] || []).length > 0 ? "Montant retenu (€)" : "Ou saisir le montant (€)"}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step={q.type === "amount" ? "0.01" : "1"}
                    value={answers[q.key] === undefined ? "" : answers[q.key]}
                    onChange={(e) => {
                      // Saisie à la main : le total des produits choisis n'a plus cours.
                      setPicked((prev) => ({ ...prev, [q.key]: [] }));
                      onAnswerChange(q.key, e.target.value);
                    }}
                  />
                </>
              )}
            </Col>
          ))}
        </Row>
      )}

      <p className="mb-1" style={{ fontSize: "0.9rem" }}>{explanation}</p>
      {warning && <p className="text-muted mb-1" style={{ fontSize: "0.82rem" }}>{warning}</p>}

      {canApply && (
        <div className="d-flex align-items-center" style={{ gap: 10 }}>
          <b>
            {cagnotteAmount > 0 ? euros(cagnotteAmount) : "Aucun crédit"}
            {(productOptions || []).length > 0 && ` + ${productOptions.map((o) => o.label).join(", ")}`}
          </b>
          <Button size="sm" variant="success" onClick={onApply}>Appliquer</Button>
        </div>
      )}
    </div>
  );
};

BaremeSuggestion.propTypes = {
  suggestion: PropTypes.object,
  answers: PropTypes.object,
  onAnswerChange: PropTypes.func,
  onApply: PropTypes.func,
  orderItems: PropTypes.array,
  onShowBareme: PropTypes.func,
  // Les lignes « Ce qui est concerné », à plat, à chaque changement.
  onPickedChange: PropTypes.func,
};

BaremeSuggestion.defaultProps = {
  answers: {},
  orderItems: [],
};

export default BaremeSuggestion;
